import { createContext, useContext, useId, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { AgainLine, EmptyState, LastUpdate, Recovery, RetryAction, TechnicalDetails } from '@beyond-js/ui/react';
import { useI18n } from '@cdn/i18n';
import { useObservable } from '@cdn/state';
import { Outages } from './outages';

const Scope = createContext<Outages | null>(null);

/** The notice's words in each language, made once: new words would make a new notice */
const words = { en: {}, es: { ...RetryAction.labels.es, ...AgainLine.labels.es, ...TechnicalDetails.labels.es } };

/** The language code the shared components take */
function useCode(): 'en' | 'es' {
	return useI18n().locale.code === 'es' ? 'es' : 'en';
}

/**
 * The outages of the pages below it (decision D163): the signed-in frame holds one, and a dialog holds none
 * (`none`), so a read there says its outage where it is rather than behind the dialog.
 */
export function OutageScope({ none = false, children }: { none?: boolean; children: ReactNode }) {
	const outages = useMemo(() => (none ? null : new Outages()), [none]);
	return <Scope.Provider value={outages}>{children}</Scope.Provider>;
}

interface Held {
	/** What the read is, as a noun: said only in the notice's Technical details */
	name: string;
	/** What its last load threw, when it failed */
	problem: unknown;
	/** Whether it shows content kept from an earlier read */
	kept?: boolean;
	/** The read again: settles once tried */
	run: () => Promise<unknown>;
	/** Held while it answers too, so the page knows when nothing could be read; false for a read held only while it fails */
	present?: boolean;
}

/**
 * Holds one read of the page in its register while it is mounted: who did not answer and its evidence while it
 * does not answer, and nothing more while it does. Returns the register, or null where the read says its outage
 * in place (a dialog, a page outside the signed-in frame).
 */
export function useOutage({ name, problem, kept = false, run, present = true }: Held): Outages | null {
	const outages = useContext(Scope);
	const key = useId();
	const latest = useRef(run);
	latest.current = run;
	const cause = Outages.cause(problem);
	const details = cause ? Outages.details(problem) : null;
	useLayoutEffect(() => {
		if (!outages) return;
		if (!present && !cause) return void outages.delete(key);
		outages.set(key, { title: name, cause, kept, details, run: () => latest.current() });
		// The evidence is compared by its values, which change with every try
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [outages, key, name, cause, kept, present, details?.request, details?.time, details?.text]);
	useLayoutEffect(() => () => outages?.delete(key), [outages, key]);
	return outages;
}

/**
 * The page's outages, said once (D163): after the page's header, on the H1's edge, one line per who did not
 * answer in the warning tone, with when it last tried and one Try again, listing nothing; the reads' evidence is
 * its one Technical details. When nothing on the page could be read the line is the page's body, in
 * `Unavailable`'s form. A page header draws it; `fallback` draws it at the top of a page that has none.
 */
export function OutageNotices({ fallback = false }: { fallback?: boolean }) {
	const outages = useContext(Scope);
	return outages ? <Notices outages={outages} fallback={fallback} /> : null;
}

function Notices({ outages, fallback }: { outages: Outages; fallback: boolean }) {
	const { t, locale } = useI18n();
	const code = useCode();
	const { said, whole, placed } = useObservable(outages);
	useLayoutEffect(() => (fallback ? undefined : outages.region()), [outages, fallback]);
	if (!said.length || (fallback && placed)) return null;
	return (
		<>
			{said.map(notice => (
				<Recovery key={notice.cause} title={Outages.say(notice.cause, t)} reads={notice.reads} whole={whole} labels={words[code]} locale={locale.code} />
			))}
		</>
	);
}

/**
 * The page's body, which the outage replaces when nothing on the page could be read (D163): its regions stay
 * mounted, so their reads go on being read, and come back in place once one of them answers.
 */
export function OutageBody({ children }: { children: ReactNode }) {
	const outages = useContext(Scope);
	return outages ? <Body outages={outages}>{children}</Body> : <>{children}</>;
}

function Body({ outages, children }: { outages: Outages; children: ReactNode }) {
	const { whole } = useObservable(outages);
	useLayoutEffect(() => outages.body(), [outages]);
	// No box of its own: its regions keep the page's layout until the outage replaces them
	return (
		<div data-outage-body="" hidden={whole} style={{ display: whole ? 'none' : 'contents' }}>
			{children}
		</div>
	);
}

/**
 * A region whose read did not answer, in its own place (D163): "Not available right now" with the outage's mark,
 * never empty, and with no cause or Try again of its own, which the page's notice says once: one quiet line, unless
 * `frame` asks for a box (0.24.1).
 */
export function OutagePlace({ frame }: { frame?: boolean }) {
	const code = useCode();
	return <EmptyState kind="outage" frame={frame} labels={EmptyState.labels[code]} />;
}

/**
 * Content kept from an earlier read while its read does not answer (D163): it stays, with "Last updated 12:04"
 * above it, or "May be out of date" when the time it was read is not known, which is never invented.
 */
export function Kept({ time, children }: { time?: number; children: ReactNode }) {
	const { locale } = useI18n();
	const code = useCode();
	return (
		<>
			<LastUpdate time={time ?? null} locale={locale.code} labels={LastUpdate.labels[code]} />
			{children}
		</>
	);
}

/**
 * An outage said where its read is, for a read no page notice holds (D163): one line in a dialog, or, for a read
 * nothing of the page shows without (`whole`), the page's body in `Unavailable`'s form. Its title is who did not
 * answer; its one row of Technical details is the read's evidence; it reads again by itself and on Try again.
 */
export function OutageLine({ name, problem, run, whole = false }: { name: string; problem: unknown; run: () => Promise<unknown>; whole?: boolean }) {
	const { t, locale } = useI18n();
	const code = useCode();
	const cause = Outages.cause(problem) ?? 'cdn';
	const reads = [{ key: 'read', title: name, details: Outages.details(problem), run }];
	return <Recovery title={Outages.say(cause, t)} reads={reads} whole={whole} labels={words[code]} locale={locale.code} />;
}
